import { useEffect, useRef, useState } from 'react'

import { UPLOAD_IMAGE_ROTATION_STEP } from '../../UploadImage.constants'

import { useCropDrag } from './useCropDrag'
import { FULL_ROTATION } from './useImageCropEngine.constants'
import {
  getImageLayout,
  getInitialCrop,
  getRefitCrop,
  getRotatedCrop,
  renderCroppedImage,
} from './useImageCropEngine.utils'
import { useStageImageSource } from './useStageImageSource'

// Owns the crop preview: the decoded image (useStageImageSource), crop frame +
// rotation, pointer drag/resize (useCropDrag) and the canvas export on Save.
// The responsive stage size is measured by the modal and passed in.
export function useImageCropEngine({
  aspect,
  cropShape,
  initialFile,
  initialImageUrl,
  stageHeight,
  stageWidth,
}) {
  const {
    fileName,
    handleRestoreOpenSource,
    handleSelectFile: handleSelectSourceFile,
    image,
    isImageLoading,
    isLoadError,
    naturalSize,
    setImageLoading,
    source,
  } = useStageImageSource({ initialFile, initialImageUrl })

  const [rotation, setRotation] = useState(0)
  const [crop, setCrop] = useState(null)
  const [isDirty, setDirty] = useState(false)

  // Read in effects only, so the crop effect can tell "new image" from "stage
  // resized" without re-triggering on unrelated changes.
  const cropSourceRef = useRef(null)
  const prevStageRef = useRef(null)
  const rotationRef = useRef(rotation)

  useEffect(() => {
    rotationRef.current = rotation
  }, [rotation])

  // Init the crop for a freshly decoded image; re-fit it on stage resize.
  useEffect(() => {
    if (naturalSize && stageWidth && stageHeight) {
      const prevStage = prevStageRef.current
      const isNewImage = cropSourceRef.current !== source
      const isStageResized =
        prevStage &&
        (prevStage.width !== stageWidth || prevStage.height !== stageHeight)

      if (isNewImage) {
        // A new image opens unrotated: largest centered crop, loader off.
        const nextLayout = getImageLayout({
          naturalHeight: naturalSize.height,
          naturalWidth: naturalSize.width,
          rotation: 0,
          stageHeight,
          stageWidth,
        })

        setRotation(0)
        setCrop(getInitialCrop({ aspect, layout: nextLayout }))
        setImageLoading(false)
      } else if (isStageResized) {
        const nextLayout = getImageLayout({
          naturalHeight: naturalSize.height,
          naturalWidth: naturalSize.width,
          rotation: rotationRef.current,
          stageHeight,
          stageWidth,
        })
        const prevLayout = getImageLayout({
          naturalHeight: naturalSize.height,
          naturalWidth: naturalSize.width,
          rotation: rotationRef.current,
          stageHeight: prevStage.height,
          stageWidth: prevStage.width,
        })

        setCrop(previousCrop =>
          getRefitCrop({ aspect, nextLayout, previousCrop, prevLayout })
        )
      }

      cropSourceRef.current = source
      prevStageRef.current = { height: stageHeight, width: stageWidth }
    }
  }, [aspect, naturalSize, setImageLoading, source, stageHeight, stageWidth])

  const layout =
    naturalSize && stageWidth && stageHeight
      ? getImageLayout({
          naturalHeight: naturalSize.height,
          naturalWidth: naturalSize.width,
          rotation,
          stageHeight,
          stageWidth,
        })
      : null

  function handleChangeCrop(nextCrop) {
    setCrop(nextCrop)
    setDirty(true)
  }

  const drag = useCropDrag({
    aspect,
    crop,
    layout,
    onCropChange: handleChangeCrop,
  })

  function handleSelectFile(file) {
    handleSelectSourceFile(file)
    setDirty(true)
  }

  // Re-fits to the stage but keeps the current crop size (clamped to the
  // rotated bounds) instead of resetting it.
  function handleRotate() {
    if (naturalSize && crop && layout) {
      const nextRotation =
        (rotation + UPLOAD_IMAGE_ROTATION_STEP) % FULL_ROTATION

      const nextLayout = getImageLayout({
        naturalHeight: naturalSize.height,
        naturalWidth: naturalSize.width,
        rotation: nextRotation,
        stageHeight,
        stageWidth,
      })

      setRotation(nextRotation)
      setCrop(
        getRotatedCrop({ aspect, layout: nextLayout, previousCrop: crop })
      )
      setDirty(true)
    }
  }

  // Restoring a replaced image reloads it (the crop effect then re-inits);
  // otherwise reset rotation/crop in place.
  function handleUndo() {
    if (
      !handleRestoreOpenSource() &&
      naturalSize &&
      stageWidth &&
      stageHeight
    ) {
      const baseLayout = getImageLayout({
        naturalHeight: naturalSize.height,
        naturalWidth: naturalSize.width,
        rotation: 0,
        stageHeight,
        stageWidth,
      })

      setRotation(0)
      setCrop(getInitialCrop({ aspect, layout: baseLayout }))
    }

    setDirty(false)
  }

  function getCroppedImage() {
    return renderCroppedImage({
      crop,
      cropShape,
      image,
      layout,
      naturalHeight: naturalSize.height,
      naturalWidth: naturalSize.width,
      rotation,
    })
  }

  return {
    crop,
    cropShape,
    dragCursor: drag.dragCursor,
    fileName,
    getCroppedImage,
    handleRotate,
    handleSelectFile,
    handleUndo,
    hasImage: Boolean(image),
    image,
    isDirty,
    isDragging: drag.isDragging,
    isImageLoading,
    isLoadError,
    layout,
    rotation,
    onPointerDown: drag.onPointerDown,
    onPointerMove: drag.onPointerMove,
    onPointerUp: drag.onPointerUp,
  }
}
